import { LicenseRequest, LicenseTypeQuestion } from "./models";
import { placeholders } from "./placeholders";

const interpolateContract = (
  licenseRequest: any,
  contract: string,
  batched: boolean
) => {
  contract = contract || "";
  const placeholderKeys = Object.keys(placeholders);
  for (const k in placeholderKeys) {
    for (const p in placeholders[placeholderKeys[k]]) {
      // Things like Licensee.Address1 or Product.Songwriters
      const re = new RegExp(
        "&lt;%" + placeholders[placeholderKeys[k]][p].placeholder + "%&gt;",
        "g"
      );

      let licenseRequestId = licenseRequest.id;
      if (batched) {
        licenseRequestId = `(${licenseRequest.batchedLicenseRequests
          .map((lr: LicenseRequest) => lr.id)
          .join("|")})`;
      }

      const re2 = new RegExp(
        `(<span data-span-type\="${
          placeholders[placeholderKeys[k]][p].placeholder
        }" data-license-request-id\="${licenseRequestId}">[^<>]*<\/span>)`,
        "g"
      );
      let replaceText = "";
      try {
        replaceText = `<span data-span-type="${
          placeholders[placeholderKeys[k]][p].placeholder
        }" data-license-request-id="${licenseRequest.id}">${
          placeholders[placeholderKeys[k]][p].getReplaceText(licenseRequest) ||
          ""
        }</span>`;
        if (replaceText === undefined) {
          console.error(
            placeholders[placeholderKeys[k]][p].text + " returned undefined"
          );
        }
      } catch (err) {
        console.error("error getting replace text", {
          err,
          key: placeholderKeys[k],
          placeholder: placeholders[placeholderKeys[k]][p],
        });
      }
      contract = contract.replace(re, replaceText);
      if (placeholders[placeholderKeys[k]][p].shouldReplaceSpans === true) {
        contract = contract.replace(re2, replaceText);
      }
    }
  }
  const questions = licenseRequest.licenseType.licenseTypeQuestions.filter(
    (q: LicenseTypeQuestion) => !q.autoPopulated
  );
  for (const q in questions) {
    // Questions asked of the customer and of the admin
    // The internal questions may change on the AdminLicenseRequestDetails page
    const re1 = new RegExp(
      `&lt;%QUESTION.${questions[q].questionName}%&gt;`,
      "g"
    );
    const re2 = new RegExp(
      `(<span data-span-type\="${questions[q].questionName}" data-license-request-id\="${licenseRequest.id}">[^<>]*<\/span>)`,
      "g"
    );
    let replaceText = `<span data-span-type="${questions[q].questionName}" data-license-request-id="${licenseRequest.id}"></span>`;
    try {
      let answer =
        questions[q].licenseTypeAnswers.length > 0
          ? questions[q].licenseTypeAnswers[0].answer || ""
          : "";
      if (questions[q].answerType === "Date") {
        let d = new Date(answer);
        const userTimezoneOffset = d.getTimezoneOffset() * 60000;
        d = new Date(d.getTime() + userTimezoneOffset);
        if (licenseRequest.licenseType.location === "US") {
          answer = `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d
            .getDate()
            .toString()
            .padStart(2, "0")}/${d.getFullYear()}`;
        }
        if (licenseRequest.licenseType.location === "UK") {
          answer = `${d.getDate().toString().padStart(2, "0")}/${(
            d.getMonth() + 1
          )
            .toString()
            .padStart(2, "0")}/${d.getFullYear()}`;
        }
      }
      replaceText = `<span data-span-type="${questions[q].questionName}" data-license-request-id="${licenseRequest.id}">${answer}</span>`;
      if (!replaceText) {
        replaceText = `<span data-span-type="${questions[q].questionName}" data-license-request-id\="${licenseRequest.id}"></span>`;
      }
    } catch (err) {
      console.error(`error getting answer`, { err, question: questions[q] });
    }

    contract = contract.replace(re1, replaceText);
    contract = contract.replace(re2, replaceText);

    if (questions[q].internal) {
      const reSum1 = new RegExp(
        `&lt;%SUM.${questions[q].questionName}%&gt;`,
        "g"
      );
      const reSum2 = new RegExp(
        `(<span data-span-type\="sum-${questions[q].questionName}">[^<>]*<\/span>)`,
        "g"
      );
      let replaceText = `<span data-span-type="sum-${questions[q].questionName}"></span>`;
      try {
        const currentRequestQuestion =
          licenseRequest.licenseType &&
          licenseRequest.licenseType.licenseTypeQuestions
            ? licenseRequest.licenseType.licenseTypeQuestions.find(
                (curQ: LicenseTypeQuestion) =>
                  curQ.questionName === questions[q].questionName
              )
            : null;
        let sum = 0;
        if (currentRequestQuestion) {
          sum =
            currentRequestQuestion.licenseTypeAnswers &&
            currentRequestQuestion.licenseTypeAnswers.length > 0
              ? parseFloat(currentRequestQuestion.licenseTypeAnswers[0].answer)
              : 0;
        }
        if (batched) {
          sum += licenseRequest.batchedLicenseRequests
            ? licenseRequest.batchedLicenseRequests
                .filter((lr: LicenseRequest) => lr.id !== licenseRequest.id)
                .reduce((acc: number, cur: LicenseRequest) => {
                  const matchingQuestion =
                    cur.licenseType.licenseTypeQuestions.find(
                      (sumQ) => sumQ.questionName === questions[q].questionName
                    );
                  if (
                    matchingQuestion &&
                    matchingQuestion.licenseTypeAnswers &&
                    matchingQuestion.licenseTypeAnswers.length > 0
                  ) {
                    return (
                      acc +
                      parseFloat(matchingQuestion.licenseTypeAnswers[0].answer)
                    );
                  } else {
                    return 0;
                  }
                }, 0)
            : 0;
        }
        replaceText = `<span data-span-type="sum-${
          questions[q].questionName
        }">${sum.toFixed(2)}</span>`;
      } catch (err) {
        console.error(
          `error calculating sum for ${questions[q].questionName}`,
          { err }
        );
      }
      contract = contract.replace(reSum1, replaceText);
      contract = contract.replace(reSum2, replaceText);
    }
  }

  // handle conditional blocks
  // added 11/4/24
  // adds ability to hide/show blocks of text based on the value of a question

  // looks for an opening block, wich is an empty <span> tag like this:
  // ** NOTE: ** it is important that it's not a self-closing tag. The TinyMCE editor
  // will wrap it in additional <span> and <p> tags, which will throw off the regex
  // ex. <span data-span-type="begin-condition" data-condition="exampleQuestionName"></span>
  const conditionalSpanMatchPattern =
    /<span data-span-type="begin-condition"[^>]*>/g;
  const conditionalSpanMatches = contract.match(conditionalSpanMatchPattern);

  if (conditionalSpanMatches) {
    // for each opening block, find the condition it is based on
    for (const conditionalSpanMatch of conditionalSpanMatches) {
      const beginConditionMatch = conditionalSpanMatch;

      const conditions = beginConditionMatch.match(/data-condition="([^"]+)"/);

      if (conditions && conditions[1]) {
        const condition = conditions[1];

        // based off of the condition, find the associated question
        // by questionName
        const relevantQuestion = questions.find(
          (q: LicenseTypeQuestion) => q.questionName === condition
        );

        let conditionalAnswer = false;
        let relevantAnswer = "";
        // find the associated answer for the question
        if (
          relevantQuestion &&
          relevantQuestion.licenseTypeAnswers.length > 0
        ) {
          relevantAnswer = relevantQuestion.licenseTypeAnswers[0].answer;
        }
        // if question type is a dropdown,
        // special handling for getting a boolean value
        // from "Yes/No" text
        if (relevantQuestion && relevantQuestion.answerType === "Dropdown") {
          if (relevantAnswer === "Yes") {
            conditionalAnswer = true;
          }
        } else {
          // otherwise, just parse relevantAnswer as boolean
          conditionalAnswer = Boolean(relevantAnswer);
        }
        // Note: :point_up: the answer parsing portion of this addition is
        // the piece that is most likely to need further change. This handles
        // the use case at hand, but it is entirely possible that we may need
        // to handle other types of questions/answerTypes in the future.

        // find the closing tag for the block. It will have a very similar
        // convention to what's used for the opening tag and will share the
        // same data-condition attribute
        // ex. <span data-span-type="end-condition" data-condition="exampleQuestionName"></span>
        const endConditionMatchPattern = `<span data-span-type="end-condition" data-condition="${condition}">`;
        const endConditionMatches = contract.match(endConditionMatchPattern);

        // if we're able to find an end condition <span> tag, we then try to target
        // the entire block of text between the begin and end condition <span> tags
        if (endConditionMatches && endConditionMatches.length > 0) {
          // Create regex pattern to match all content between begin and end condition spans
          const conditionalBlockPattern = new RegExp(
            `${beginConditionMatch}([\\s\\S]*?)${endConditionMatchPattern}`,
            "g"
          );

          // if the condition for the block is not met, remove the block of text
          if (!conditionalAnswer) {
            contract = contract.replace(conditionalBlockPattern, "");
          }
        }
      }
    }
  }

  // Batch stuff
  // First, replace the placeholder with empty spans. This will only happen in the first instance of a batch.
  const loopTypes = ["SCHEDULE_SONGS_PER_PROJECT", "INVOICE_SONGS_PER_PROJECT"];
  for (const l in loopTypes) {
    const loopType = loopTypes[l];
    const reStartLoop = new RegExp(`&lt;%LOOP.${loopType}_START%&gt;`, "g");
    const reEndLoop = new RegExp(`&lt;%LOOP.${loopType}_END%&gt;`, "g");
    contract = contract.replace(
      reStartLoop,
      `<span data-span-type="${loopType}_START" data-license-request-id="${licenseRequest.id}"></span>`
    );
    contract = contract.replace(
      reEndLoop,
      `<span data-span-type="${loopType}_END" data-license-request-id="${licenseRequest.id}"></span>`
    );
    const reLicenseRequest = new RegExp(
      `\<span data-span-type\="${loopType}_START" data-license-request-id\="${licenseRequest.id}"\>\<\/span\>`
    );
    if (!reLicenseRequest.test(contract)) {
      // If this license request is NOT already represented in the contract

      /*
      Regex reminders:
      \d = shortcut for [0-9]
      + = one or more instances of the pattern
      .|\n = any character including new lines
      * = zero or more instances of the pattern
      ? = lazy mode - so that it only grabs one song block
      */
      // Find the guts of the repeatable stuff
      if (
        licenseRequest.batchedLicenseRequests &&
        licenseRequest.batchedLicenseRequests.length > 0
      ) {
        const lastLicReqIdInBatch =
          licenseRequest.batchedLicenseRequests[
            licenseRequest.batchedLicenseRequests.length - 1
          ].id;

        /*
         * This tempContract is here merely to get the reSongGuts. It will fill out the whole contract
         * but we really only need the looped part.
         */
        // eslint-disable-next-line prefer-const
        let tempContract = interpolateContract(
          { ...licenseRequest, batchedLicenseRequests: [] },
          licenseRequest.licenseType.contract,
          false
        );
        const reSongGuts = new RegExp(
          `<span data-span-type\="${loopType}_START" data-license-request-id\="${licenseRequest.id}"\>\<\/span\>((.|\n)*?)\<span data-span-type\="${loopType}_END" data-license-request-id=\"${licenseRequest.id}"\>\<\/span\>`
        );
        // eslint-disable-next-line prefer-const
        let match = reSongGuts.exec(tempContract);

        if (match && match[0]) {
          // Fill it in with the correct info for this license request
          let newGuts = match[0];
          // replace data-license-request-id on all tags
          newGuts = newGuts.replace(
            /data-license-request-id="\d+"/g,
            `data-license-request-id="${licenseRequest.id}"`
          );
          // replace LICENSE.NUMBER
          newGuts = newGuts.replace(
            /(\<span data-span-type\="LICENSE.NUMBER" data-license-request-id="\d+"\>)\d+(\<\/span\>)/,
            `$1${licenseRequest.id}$2`
          );
          // replace placeholders and questions in the newGuts
          for (const k in placeholderKeys) {
            for (const p in placeholders[placeholderKeys[k]]) {
              const re = new RegExp(
                `(<span data-span-type\="${
                  placeholders[placeholderKeys[k]][p].placeholder
                }" data-license-request-id="[0-9]+">[^<>]*<\/span>)`,
                "g"
              );
              let replaceText = `<span data-span-type="${
                placeholders[placeholderKeys[k]][p].placeholder
              }" data-license-request-id="${licenseRequest.id}"></span>`;
              try {
                replaceText = `<span data-span-type="${
                  placeholders[placeholderKeys[k]][p].placeholder
                }" data-license-request-id="${licenseRequest.id}">${
                  placeholders[placeholderKeys[k]][p].getReplaceText(
                    licenseRequest
                  ) || ""
                }</span>`;
              } catch (err) {
                console.error("error in replacing batch placeholder", {
                  err,
                  key: placeholderKeys[k],
                  placeholder: placeholders[placeholderKeys[k]][p],
                });
              }
              newGuts = newGuts.replace(re, replaceText);
            }
          }
          const questions =
            licenseRequest.licenseType.licenseTypeQuestions.filter(
              (q: LicenseTypeQuestion) => !q.autoPopulated
            );
          for (const q in questions) {
            const re = new RegExp(
              `(<span data-span-type\="${questions[q].questionName}" data-license-request-id\="${licenseRequest.id}">[^<>]*<\/span>)`,
              "g"
            );
            let replaceText = `<span data-span-type="${questions[q].questionName}" data-license-request-id="${licenseRequest.id}"></span>`;
            try {
              replaceText = `<span data-span-type="${questions[q].questionName}" data-license-request-id="${licenseRequest.id}">${questions[q].licenseTypeAnswers[0].answer}</span>`;
            } catch (err) {
              console.error(`error getting answer`, {
                err,
                question: questions[q],
              });
            }
            newGuts = newGuts.replace(re, replaceText);
          }

          // Place the new guts at the end of the list of songs
          const reEndTag = new RegExp(
            `\<span data-span-type\="${loopType}_END" data-license-request-id\="${lastLicReqIdInBatch}"\>\<\/span\>`,
            "g"
          );
          const allOccurencesOfEndTag = contract.match(reEndTag);
          if (allOccurencesOfEndTag) {
            const lastOccurenceOfEndTag =
              allOccurencesOfEndTag[allOccurencesOfEndTag.length - 1];
            contract = contract.replace(
              lastOccurenceOfEndTag,
              `${lastOccurenceOfEndTag}${newGuts}`
            );
          }
        }
      }
    }
  }

  return contract;
};

export { interpolateContract };
